import { Eye, EyeOff } from 'lucide-preact';
import { route } from 'preact-router';
import { useEffect, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { FIELD_LABEL_CLASS } from '../../components/ui/fieldLabel.js';
import { api } from '../../services/api.js';
import {
  PASSWORD_INVALID_MESSAGE,
  PASSWORD_RULE_TEXT,
  satisfiesPasswordPolicy,
} from '../../utils/validation.js';

interface ResetPasswordPageProps {
  path?: string;
}

export function ResetPasswordPage(_props: ResetPasswordPageProps) {
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('token');
    if (!t) {
      route('/account/forgot-password');
      return;
    }
    setToken(t);
  }, []);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!token) return;

    // 140 US3: the same rule the boundary applies. Until this feature the reset path had NO
    // server-side rule, so this screen could set a password the change-password screen would have
    // refused — the bypass that made the rule decorative. The server enforces it now too; this is
    // here so the person is told before submitting rather than by a refusal.
    if (!satisfiesPasswordPolicy(password)) {
      setPasswordError(PASSWORD_INVALID_MESSAGE);
      return;
    }
    setPasswordError('');

    setLoading(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { token, password });
      route('/account/login?reset=1');
    } catch (err) {
      setError((err as { message?: string }).message ?? 'This reset link has expired.');
    } finally {
      setLoading(false);
    }
  }

  if (!token) return null;

  return (
    <div className="page-fill bg-bg flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-3xl font-bold text-text mb-2">Set New Password</h1>
        <p className="text-gray-400 mb-8">Choose a new password for your account.</p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label for="rp-password" className={FIELD_LABEL_CLASS}>
              New Password
            </label>
            <div className="relative">
              <input
                id="rp-password"
                data-testid="new-password"
                {...(showPassword ? { type: 'text' as const } : { type: 'password' as const })}
                required
                minLength={8}
                aria-invalid={passwordError ? 'true' : undefined}
                aria-describedby={
                  passwordError ? 'rp-password-error rp-password-rule' : 'rp-password-rule'
                }
                className="bg-surface-mid text-text rounded-lg px-4 py-3 w-full pr-11"
                value={password}
                onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff size={18} aria-hidden="true" />
                ) : (
                  <Eye size={18} aria-hidden="true" />
                )}
              </button>
            </div>
            {/* Stated up front (FR-015) — the rule is the platform's, not this screen's opinion. */}
            <span id="rp-password-rule" className="text-xs text-text-muted">
              {PASSWORD_RULE_TEXT}
            </span>
            {passwordError ? (
              <span
                id="rp-password-error"
                data-testid="new-password-error"
                role="alert"
                className="text-sm text-error"
              >
                {passwordError}
              </span>
            ) : null}
          </div>
          {error && (
            <div className="text-sm text-red-400">
              <p>{error}</p>
              <button
                type="button"
                className="underline mt-1"
                onClick={() => route('/account/forgot-password')}
              >
                Request a new link
              </button>
            </div>
          )}
          <Button type="submit" loading={loading} className="w-full mt-2">
            Set New Password
          </Button>
        </form>
      </div>
    </div>
  );
}
